import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, FileText, Clock, CheckCircle, AlertTriangle, TrendingUp, DollarSign, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';

const ProcurementDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dashboardData, setDashboardData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getProcurementDashboard(businessId);
      
      if (response.data?.success) {
        setDashboardData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load dashboard data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      approved: { variant: 'default' as const, label: 'Approved' },
      rejected: { variant: 'destructive' as const, label: 'Rejected' },
      completed: { variant: 'default' as const, label: 'Completed' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Procurement Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of procurement activities and performance
          </p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(dashboardData.totalSpend || 0)}</div>
            <p className="text-xs text-muted-foreground">
              +{dashboardData.spendGrowth || 0}% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active POs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.activePOs || 0}</div>
            <p className="text-xs text-muted-foreground">
              {dashboardData.pendingPOs || 0} pending approval
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Requisitions</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.pendingRequisitions || 0}</div>
            <p className="text-xs text-muted-foreground">
              {dashboardData.overdueRequisitions || 0} overdue
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Supplier Performance</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.avgSupplierRating || 0}/5</div>
            <p className="text-xs text-muted-foreground">
              {dashboardData.onTimeDelivery || 0}% on-time delivery
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Purchase Orders */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Purchase Orders</CardTitle>
            <CardDescription>Latest purchase order activities</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData.recentPOs && dashboardData.recentPOs.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.recentPOs.slice(0, 5).map((po: any) => (
                    <TableRow 
                      key={po.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                    >
                      <TableCell className="font-medium">{po.poNumber}</TableCell>
                      <TableCell>{po.supplier?.name}</TableCell>
                      <TableCell>{formatCurrency(po.totalAmount || 0)}</TableCell>
                      <TableCell>{getStatusBadge(po.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-4">No recent purchase orders</p>
            )}
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
            <CardDescription>Items requiring your attention</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData.pendingApprovals && dashboardData.pendingApprovals.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.pendingApprovals.slice(0, 5).map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{formatCurrency(item.amount || 0)}</span>
                      <Button size="sm" onClick={() => navigate(item.link)}>
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No pending approvals</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Top Suppliers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Suppliers</CardTitle>
            <CardDescription>By spend this month</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData.topSuppliers && dashboardData.topSuppliers.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.topSuppliers.slice(0, 5).map((supplier: any, index: number) => (
                  <div key={supplier.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{supplier.name}</p>
                        <p className="text-sm text-muted-foreground">{supplier.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(supplier.totalSpend || 0)}</p>
                      <p className="text-sm text-muted-foreground">{supplier.orderCount} orders</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No supplier data available</p>
            )}
          </CardContent>
        </Card>

        {/* Budget Utilization */}
        <Card>
          <CardHeader>
            <CardTitle>Budget Utilization</CardTitle>
            <CardDescription>Current month budget status</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData.budgetData && dashboardData.budgetData.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.budgetData.map((budget: any) => (
                  <div key={budget.category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{budget.category}</span>
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(budget.spent)} / {formatCurrency(budget.allocated)}
                      </span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          budget.utilization > 90 ? 'bg-red-500' : 
                          budget.utilization > 75 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(budget.utilization, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {budget.utilization}% utilized
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No budget data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common procurement tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <Button 
              onClick={() => navigate('/procurement/requisitions/create')}
              className="flex items-center gap-2 h-auto p-4"
              variant="outline"
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Create Requisition</div>
                <div className="text-xs text-muted-foreground">Request new items</div>
              </div>
            </Button>

            <Button 
              onClick={() => navigate('/procurement/purchase-orders/create')}
              className="flex items-center gap-2 h-auto p-4"
              variant="outline"
            >
              <ShoppingCart className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Create PO</div>
                <div className="text-xs text-muted-foreground">New purchase order</div>
              </div>
            </Button>

            <Button 
              onClick={() => navigate('/procurement/quotations/create')}
              className="flex items-center gap-2 h-auto p-4"
              variant="outline"
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Request Quote</div>
                <div className="text-xs text-muted-foreground">Get supplier quotes</div>
              </div>
            </Button>

            <Button 
              onClick={() => navigate('/procurement/supplier-mall')}
              className="flex items-center gap-2 h-auto p-4"
              variant="outline"
            >
              <Package className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Supplier Mall</div>
                <div className="text-xs text-muted-foreground">Browse catalog</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcurementDashboard; 